import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ScriptRoles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function ScriptRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [baseRole, setBaseRole] = useState({
    base_role_id: '',
    base_role_name: ''
  });
  const [formData, setFormData] = useState({
    script_name: '',
    discord_role_id: '',
    discord_role_name: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/script-roles`, {
        withCredentials: true
      });
      setRoles(response.data);
      
      // Load base role if configured
      const roleWithBase = response.data.find(r => r.base_role_id);
      if (roleWithBase) {
        setBaseRole({
          base_role_id: roleWithBase.base_role_id,
          base_role_name: roleWithBase.base_role_name
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching script roles:', error);
      setLoading(false);
    }
  };

  const handleBaseRoleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API_URL}/api/admin/script-roles/base-role`,
        baseRole,
        { withCredentials: true }
      );
      
      alert('✅ Base role configuration saved!');
      fetchRoles();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.error || 'Failed to save'}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API_URL}/api/admin/script-roles`,
        formData,
        { withCredentials: true }
      );

      alert('✅ Script role configuration saved!');
      setShowAddModal(false);
      setFormData({ script_name: '', discord_role_id: '', discord_role_name: '' });
      fetchRoles();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.error || 'Failed to save'}`);
    }
  };

  if (loading) {
    return <div className="loading">Loading script roles...</div>;
  }

  return (
    <div className="script-roles-management">
      <div className="panel-header">
        <h1>Script Role Configuration</h1>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
          <span className="emoji">➕</span> Add Role Mapping
        </button>
      </div>

      {/* Base Role Configuration */}
      <div className="base-role-config glass">
        <h2><span className="emoji">🎭</span> Base Role Settings</h2>
        <p>This role will be automatically assigned to all users when they verify their first purchase</p>
        <form onSubmit={handleBaseRoleUpdate} className="base-role-form">
          <div className="form-row">
            <div className="form-group">
              <label>Base Role ID</label>
              <input
                type="text"
                value={baseRole.base_role_id}
                onChange={(e) => setBaseRole({ ...baseRole, base_role_id: e.target.value })}
                placeholder="e.g., 1234567890123456789"
              />
            </div>
            <div className="form-group">
              <label>Base Role Name</label>
              <input
                type="text"
                value={baseRole.base_role_name}
                onChange={(e) => setBaseRole({ ...baseRole, base_role_name: e.target.value })}
                placeholder="e.g., Customer"
              />
            </div>
            <button type="submit" className="btn btn-success">
              Save Base Role
            </button>
          </div>
        </form>
      </div>

      <div className="roles-table-container glass">
        {roles.length === 0 ? (
          <div className="empty-state">
            <p>No script role mappings configured yet</p>
            <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
              Add First Mapping
            </button>
          </div>
        ) : (
          <table className="roles-table">
            <thead>
              <tr>
                <th>Script Name</th>
                <th>Discord Role ID</th>
                <th>Discord Role Name</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td className="script-name-cell">{role.script_name}</td>
                  <td className="role-id-cell">{role.discord_role_id}</td>
                  <td className="role-name-cell">{role.discord_role_name}</td>
                  <td>{new Date(role.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddModal && (
        <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div className="add-role-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Script Role Mapping</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Script Name *</label>
                  <input
                    type="text"
                    value={formData.script_name}
                    onChange={(e) => setFormData({ ...formData, script_name: e.target.value })}
                    placeholder="e.g., [ESX/QB/QBX] Realistic Smoking"
                    required
                  />
                  <small>Exact name as it appears in Tebex transaction</small>
                </div>

                <div className="form-group">
                  <label>Discord Role ID *</label>
                  <input
                    type="text"
                    value={formData.discord_role_id}
                    onChange={(e) => setFormData({ ...formData, discord_role_id: e.target.value })}
                    placeholder="e.g., 1234567890123456789"
                    required
                  />
                  <small>Right-click role in Discord (with Developer Mode) → Copy ID</small>
                </div>

                <div className="form-group">
                  <label>Discord Role Name *</label>
                  <input
                    type="text"
                    value={formData.discord_role_name}
                    onChange={(e) => setFormData({ ...formData, discord_role_name: e.target.value })}
                    placeholder="e.g., Realistic Smoking Owner"
                    required
                  />
                  <small>Display name for the role</small>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Save Mapping
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowAddModal(false)} 
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScriptRoles;
